import { thumbnailUrl } from "./tracks.js"

const appliedTracks = new WeakMap()

function createTrack(mediaEl, spec) {
    const track = document.createElement("track")

    track.kind = spec.kind
    track.label = spec.label

    if (spec.srclang) {
        track.srclang = spec.srclang
    }

    track.default = Boolean(spec.default)
    track.src = URL.createObjectURL(new Blob([spec.vtt], { type: "text/vtt" }))

    // media-chrome only attaches its <track> load listener when the media is
    // first bound; a synthetic "change" on the TextTrackList makes it re-read
    // the chapter cues once this track has loaded.
    track.addEventListener("load", () => mediaEl.textTracks.dispatchEvent(new Event("change")), { once: true })

    mediaEl.appendChild(track)

    if (spec.mode) {
        track.track.mode = spec.mode
    }

    return track
}

/**
 * Keeps mediaEl's generated <track>s in line with `specs`. A spec whose key
 * and VTT did not change keeps its element, so a subtitle track the viewer
 * switched on stays on when an unrelated track (e.g. thumbnails) updates.
 *
 * `rebuild` swaps every track for a fresh element. hls.js empties the cues of
 * every text track on the element when it attaches (TimelineController
 * _cleanTracks), and a loaded <track> never reloads, so tracks added before
 * the attach must be rebuilt after it.
 */
export function applyTextTracks(mediaEl, specs, { rebuild = false } = {}) {
    const current = appliedTracks.get(mediaEl) ?? new Map()
    const kept = new Map()

    for (const spec of specs) {
        const existing = current.get(spec.key)

        if (!rebuild && existing && existing.vtt === spec.vtt) {
            kept.set(spec.key, existing)
            current.delete(spec.key)
        }
    }

    // Outgoing tracks leave before their replacements arrive, so a kind never
    // has an old and a new track side by side.
    for (const { element } of current.values()) {
        URL.revokeObjectURL(element.src)
        element.remove()
    }

    const next = new Map()

    for (const spec of specs) {
        next.set(spec.key, kept.get(spec.key) ?? { vtt: spec.vtt, element: createTrack(mediaEl, spec) })
    }

    appliedTracks.set(mediaEl, next)
}

export function removeTextTracks(mediaEl) {
    applyTextTracks(mediaEl, [])
}

/**
 * Natural sizes of the given images; media-chrome crops thumbnails with the
 * #xywh fragment, so it needs the real size. Failed images are left out (the
 * builder then falls back to DEFAULT_THUMBNAIL_SIZE).
 */
export function measureImages(urls) {
    const unique = [...new Set(urls.filter(Boolean))]

    return Promise.all(
        unique.map(
            (url) =>
                new Promise((resolve) => {
                    const image = new Image()
                    image.onload = () => resolve([url, { width: image.naturalWidth || 1280, height: image.naturalHeight || 720 }])
                    image.onerror = () => resolve([url, null])
                    image.crossOrigin = "anonymous"
                    image.src = thumbnailUrl(url)
                }),
        ),
    ).then((entries) => new Map(entries.filter(([, size]) => size !== null)))
}
